<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'
import { useToastStore } from '@/stores/toast'
import type { ActionKey, PermissionConfig } from '@/types'
import { OP_ROLES, AGENCY_ROLES } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'

const router = useRouter()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()
const toast = useToastStore()

const activeTab = ref<'permissions' | 'agencies'>('permissions')

// ── Permission matrix ─────────────────────────────────────────────────────────

const ACTION_LABELS: Record<ActionKey, string> = {
  'incident.create_close':   'Incident — Create / Close',
  'incident.edit':           'Incident — Edit',
  'op.create':               'Operational Period — Create',
  'team.create_manage':      'Teams — Create / Manage',
  'team.update_status':      'Teams — Update Status',
  'assignment.create':       'Assignments — Create',
  'assignment.debrief':      'Assignments — Debrief',
  'personnel.checkin':       'Personnel — Check In',
  'personnel.checkout_others': 'Personnel — Check Out Others',
  'forms.edit':              'Forms — Edit',
  'forms.view':              'Forms — View',
  'clues.log':               'Clues — Log',
  'forms.export_pdf':        'Forms — Export PDF',
  'audit.view':              'Audit — View',
  'stats.view':              'Stats — View',
  'agency.logo_upload':      'Agency — Logo Upload',
  'personnel.generate_qr':   'Personnel — Generate QR',
  'personnel.invite':        'Personnel — Invite by Email',
  'subjects.generate_flyer': 'Subjects — Generate Flyer',
  'radio_log.log_entry':     'Radio Log — Log Entry',
  'radio_log.edit_entry':    'Radio Log — Edit Entry',
  'radio_log.delete_entry':  'Radio Log — Delete Entry',
  'radio_log.export_ics309': 'Radio Log — Export ICS-309',
  'iwi.declare':                    'IWI — Declare',
  'iwi.resolve':                    'IWI — Resolve',
  'iwi.checklist':                  'IWI — Checklist',
  'priority_traffic.create':        'Priority Traffic — Create Find',
  'priority_traffic.resolve':       'Priority Traffic — Resolve Find',
}

const editConfig = ref<PermissionConfig>({ ...permissionsStore.config })
const savingPermissions = ref(false)
const isDirty = ref(false)

onMounted(() => {
  editConfig.value = { ...permissionsStore.config }
})

// Sync editConfig when the Firestore snapshot arrives after mount (async delivery)
watch(
  () => permissionsStore.config,
  (newConfig) => {
    if (!isDirty.value) {
      editConfig.value = { ...newConfig }
    }
  },
  { deep: true },
)

function toggleRole(action: ActionKey, role: string) {
  isDirty.value = true
  const current = [...(editConfig.value[action] ?? [])]
  const idx = current.indexOf(role)
  if (idx === -1) {
    current.push(role)
  } else {
    current.splice(idx, 1)
  }
  editConfig.value = { ...editConfig.value, [action]: current }
}

function hasRole(action: ActionKey, role: string): boolean {
  return (editConfig.value[action] ?? []).includes(role)
}

async function savePermissions() {
  savingPermissions.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch('/api/super-admin/permissions', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ config: editConfig.value }),
    })
    const json = await res.json()
    if (json.error) throw new Error(json.error.message)
    isDirty.value = false
    toast.add('Permissions updated for all agencies', 'success', 3000)
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error', 4000)
  } finally {
    savingPermissions.value = false
  }
}

// ── Agencies tab ──────────────────────────────────────────────────────────────

interface AgencyRow {
  agency_id: string
  agency_name: string
  agency_logo: string | null
}

interface UserRow {
  user_id: string
  user_first_name: string
  user_last_name: string
  email: string
  role: string
  is_active: boolean
  is_super_admin: boolean
}

const agencies = ref<AgencyRow[]>([])
const loadingAgencies = ref(false)
const expandedAgencyId = ref<string | null>(null)
const agencyUsers = ref<Record<string, UserRow[]>>({})
const loadingUsers = ref<Record<string, boolean>>({})

const agencyRoleOptions = [
  { label: 'Member', value: 'member' },
  { label: 'Admin', value: 'admin' },
]

async function loadAgencies() {
  loadingAgencies.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch('/api/super-admin/agencies', {
      headers: { Authorization: `Bearer ${token}` },
    })
    const json = await res.json()
    if (json.error) throw new Error(json.error.message)
    agencies.value = json.data
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error', 4000)
  } finally {
    loadingAgencies.value = false
  }
}

async function toggleAgency(agencyId: string) {
  if (expandedAgencyId.value === agencyId) {
    expandedAgencyId.value = null
    return
  }
  expandedAgencyId.value = agencyId
  if (!agencyUsers.value[agencyId]) {
    await loadAgencyUsers(agencyId)
  }
}

async function loadAgencyUsers(agencyId: string) {
  loadingUsers.value = { ...loadingUsers.value, [agencyId]: true }
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/super-admin/agencies/${agencyId}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const json = await res.json()
    if (json.error) throw new Error(json.error.message)
    agencyUsers.value = { ...agencyUsers.value, [agencyId]: json.data }
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error', 4000)
  } finally {
    loadingUsers.value = { ...loadingUsers.value, [agencyId]: false }
  }
}

async function patchUserRole(uid: string, newRole: string, agencyId: string) {
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/super-admin/users/${uid}/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role: newRole }),
    })
    const json = await res.json()
    if (json.error) throw new Error(json.error.message)
    await loadAgencyUsers(agencyId)
    toast.add('Role updated', 'success', 2000)
  } catch (e: unknown) {
    toast.add((e as Error).message, 'error', 4000)
  }
}

function onTabChange(tab: 'permissions' | 'agencies') {
  activeTab.value = tab
  if (tab === 'agencies' && agencies.value.length === 0) {
    loadAgencies()
  }
}
</script>

<template>
  <div v-if="authStore.isSuperAdmin" class="h-full flex flex-col text-on-surface">
    <!-- Header -->
    <header class="bg-surface border-b border-border px-6 py-3 flex items-center gap-3 shrink-0">
      <button
        class="text-outline hover:text-on-surface transition-colors focus:outline-none"
        aria-label="Back to incidents"
        @click="router.push('/incidents')"
      >
        <span class="material-symbols-outlined text-[20px]">arrow_back</span>
      </button>
      <span class="material-symbols-outlined text-primary text-[20px]">shield</span>
      <span class="font-headline font-bold uppercase tracking-tight text-on-surface">Platform Admin</span>
    </header>

    <div class="flex-1 overflow-y-auto">
    <div class="max-w-6xl mx-auto px-4 py-8">
      <!-- Tab bar -->
      <div class="flex border-b border-border mb-8">
        <button
          :class="[
            'font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors focus:outline-none',
            activeTab === 'permissions'
              ? 'text-primary border-b-2 border-primary'
              : 'text-outline hover:text-on-surface',
          ]"
          @click="onTabChange('permissions')"
        >
          Permissions
        </button>
        <button
          :class="[
            'font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors focus:outline-none',
            activeTab === 'agencies'
              ? 'text-primary border-b-2 border-primary'
              : 'text-outline hover:text-on-surface',
          ]"
          @click="onTabChange('agencies')"
        >
          Agencies
        </button>
      </div>

      <!-- Permissions tab -->
      <div v-if="activeTab === 'permissions'">
        <!-- Warning callout -->
        <div class="mb-6 bg-surface-container border-l-2 border-status-rtb px-4 py-3 flex items-start gap-3">
          <span class="material-symbols-outlined text-status-rtb text-[18px] shrink-0 mt-0.5">warning</span>
          <p class="font-mono text-xs text-on-surface-variant">
            Changes apply to <strong class="text-on-surface">all agencies</strong> immediately. Removing a role from an action will block users with that role from performing it.
          </p>
        </div>

        <!-- Permission matrix -->
        <div class="overflow-x-auto bg-surface-container-low">
          <table class="w-full text-xs">
            <thead>
              <tr class="border-b border-border">
                <th class="sticky left-0 z-10 bg-surface-container-low text-left px-4 py-3 font-mono text-outline uppercase tracking-widest min-w-56">
                  Action
                </th>
                <!-- Incident role columns -->
                <th
                  v-for="role in OP_ROLES"
                  :key="role"
                  class="px-3 py-3 font-mono text-outline uppercase tracking-widest whitespace-nowrap"
                >
                  {{ role }}
                </th>
                <!-- Divider -->
                <th class="px-2 py-3 text-outline select-none" aria-hidden="true">|</th>
                <!-- Agency role columns -->
                <th
                  v-for="role in AGENCY_ROLES"
                  :key="role"
                  class="px-3 py-3 font-mono text-outline uppercase tracking-widest whitespace-nowrap"
                >
                  {{ role }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(label, action) in ACTION_LABELS"
                :key="action"
                class="border-b border-border last:border-0 hover:bg-surface-container"
              >
                <td class="sticky left-0 z-10 bg-surface-container-low px-4 py-2.5 font-mono text-on-surface-variant hover:bg-surface-container">
                  {{ label }}
                </td>
                <!-- Incident role checkboxes -->
                <td
                  v-for="role in OP_ROLES"
                  :key="role"
                  class="px-3 py-2.5 text-center"
                >
                  <input
                    type="checkbox"
                    :checked="hasRole(action as ActionKey, role)"
                    class="w-3.5 h-3.5 cursor-pointer accent-primary"
                    @change="toggleRole(action as ActionKey, role)"
                  />
                </td>
                <!-- Divider cell -->
                <td class="px-2 py-2.5 text-center text-border select-none" aria-hidden="true">|</td>
                <!-- Agency role checkboxes -->
                <td
                  v-for="role in AGENCY_ROLES"
                  :key="role"
                  class="px-3 py-2.5 text-center"
                >
                  <input
                    type="checkbox"
                    :checked="hasRole(action as ActionKey, role)"
                    class="w-3.5 h-3.5 cursor-pointer accent-primary"
                    @change="toggleRole(action as ActionKey, role)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-6 flex justify-end">
          <FosButton :disabled="savingPermissions" @click="savePermissions">
            <span class="material-symbols-outlined text-[16px]">save</span>
            {{ savingPermissions ? 'Saving…' : 'Save Permissions' }}
          </FosButton>
        </div>
      </div>

      <!-- Agencies tab -->
      <div v-if="activeTab === 'agencies'">
        <div v-if="loadingAgencies" class="flex items-center justify-center py-16 text-outline">
          <span class="material-symbols-outlined animate-spin text-[32px]">progress_activity</span>
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="agency in agencies"
            :key="agency.agency_id"
            class="bg-surface-container-low overflow-hidden"
          >
            <!-- Agency row -->
            <button
              class="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-container transition-colors focus:outline-none text-left"
              @click="toggleAgency(agency.agency_id)"
            >
              <div class="flex items-center gap-3">
                <img
                  v-if="agency.agency_logo"
                  :src="agency.agency_logo"
                  alt=""
                  class="h-8 w-8 object-contain"
                />
                <span v-else class="material-symbols-outlined text-outline">business</span>
                <div>
                  <p class="font-mono text-xs font-bold text-on-surface uppercase tracking-wide">
                    {{ agency.agency_name || '(unnamed)' }}
                  </p>
                  <p class="font-mono text-[0.6rem] text-outline mt-0.5">{{ agency.agency_id }}</p>
                </div>
              </div>
              <span class="material-symbols-outlined text-outline text-[18px]">
                {{ expandedAgencyId === agency.agency_id ? 'expand_less' : 'expand_more' }}
              </span>
            </button>

            <!-- Users panel -->
            <div v-if="expandedAgencyId === agency.agency_id" class="border-t border-border">
              <div v-if="loadingUsers[agency.agency_id]" class="py-6 flex justify-center text-outline">
                <span class="material-symbols-outlined animate-spin">progress_activity</span>
              </div>
              <table v-else class="w-full text-xs">
                <thead>
                  <tr class="border-b border-border">
                    <th class="text-left px-4 py-2 font-mono text-outline uppercase tracking-widest">Name</th>
                    <th class="text-left px-4 py-2 font-mono text-outline uppercase tracking-widest">Email</th>
                    <th class="text-left px-4 py-2 font-mono text-outline uppercase tracking-widest">Role</th>
                    <th class="text-left px-4 py-2 font-mono text-outline uppercase tracking-widest">Status</th>
                    <th class="text-left px-4 py-2 font-mono text-outline uppercase tracking-widest">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="user in agencyUsers[agency.agency_id] ?? []"
                    :key="user.user_id"
                    class="border-b border-border last:border-0 hover:bg-surface-container"
                  >
                    <td class="px-4 py-2.5 font-mono text-on-surface">
                      {{ [user.user_first_name, user.user_last_name].filter(Boolean).join(' ') || '—' }}
                    </td>
                    <td class="px-4 py-2.5 font-mono text-on-surface-variant">{{ user.email }}</td>
                    <td class="px-4 py-2.5">
                      <FosSelect
                        :model-value="user.role"
                        :options="agencyRoleOptions"
                        @update:model-value="patchUserRole(user.user_id, $event, agency.agency_id)"
                      />
                    </td>
                    <td class="px-4 py-2.5">
                      <span
                        :class="[
                          'font-mono text-[0.6rem] uppercase tracking-widest font-bold px-2 py-0.5',
                          user.is_active === false
                            ? 'bg-error-container text-error'
                            : 'bg-surface-container-highest text-status-on-scene',
                        ]"
                      >
                        {{ user.is_active === false ? 'Inactive' : 'Active' }}
                      </span>
                    </td>
                    <td class="px-4 py-2.5">
                      <span
                        v-if="user.is_super_admin"
                        class="font-mono text-[0.6rem] uppercase tracking-widest font-bold px-2 py-0.5 bg-surface-container-highest text-status-rtb"
                      >
                        Super Admin
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <p v-if="agencies.length === 0" class="text-center py-12 font-mono text-xs text-outline uppercase tracking-widest">
            No agencies found.
          </p>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>
